import { useContext, useEffect, useMemo, useState } from "react";
import EventsApi from "../../../api/instance/EventsApi";

// Components
import Alert from "../../../component/Alert";
import WidgetCard from "../../../component/instance/widget";
import EventsTable from "../../../component/instance/table/EventsTable";
import { COLOUR_ALERTS, COLOUR_CHANGES, COLOUR_EVENTS, CHART_DATA_TYPE } from "../../../component/Constants";

// Types
import InstanceTarget from "../../../types/instance/InstanceTarget";
import CombinedChanges from "../../../types/instance/CombinedChanges";
import EventsDataTable from "../../../types/instance/tables/EventsDataTable";

// Helpers
import TimeSeriesChart from "../../../component/chart/echarts/TimeSeriesChart";
import { EChartPoint, EChartTimeSeries } from "../../../component/chart/echarts/types";
import Helper from "../../../helpers/Helper";

// Context
import { TimeRangeContext } from "../../../context/TimeRangeContext";

const Changes = (props: {
    instance: InstanceTarget,
    statementId: string,
    batchStatementId?: string,
}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [eventChartData, setEventChartData] = useState<number[][]>([])
    const [alertChartData, setAlertChartData] = useState<number[][]>([])
    const [changeChartData, setChangeChartData] = useState<number[][]>([])

    const [peakChanges, setPeakChanges] = useState<number>(0)

    const [combinedChanges, setCombinedChanges] = useState<CombinedChanges[]>([])


    const [eventsDataTable, setEventsDataTable] = useState<EventsDataTable[]>([])

    const timeRangeContext = useContext(TimeRangeContext)
    const { instance } = props

    useEffect(() => {
        const getData = async () => {
            await getChanges(Number(instance.id));
        }
        getData().then(() => setLoading(false))
    }, [instance.id])


    const getChanges = async (instanceId: number) => {

        const interval = Helper.getInterval(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, timeRangeContext.timeRange?.interval)
        const events = await EventsApi.getEventsChangesAndAlerts(timeRangeContext, instanceId)

        const combinedEvents = events.map((event: any, index: number) => {
            return {
                ...event,
                id: index,
                instanceId: event.datasourceid,
                timeslice: event.timeslice,
            }
        })

        combinedEvents.sort(function compare(a: any, b: any) {
            const aa: number = Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf();
            const bb: number = Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf();
            return bb - aa;
        });

        const alertsArray = events.filter((e: any) => e.event === 'alert')
        const eventsArray = events.filter((e: any) => e.event === 'custom')
        const changesArray = events.filter((e: any) => e.event === 'change')

        const alertsData = Helper.groupByInterval(alertsArray, interval);
        const eventsData = Helper.groupByInterval(eventsArray, interval);
        const changesData = Helper.groupByInterval(changesArray, interval);

        let eventsDataTable: EventsDataTable[] = [];
        combinedEvents.forEach((event: any) => {

            let changeType: string,
                    htmlIconCode: string = '',
                    detailsUrl: string = '',
                    color: string = '',
                    title: string = '',
                    details: string;

            const dataSourceType = event.object === 'datasource' ? 'instance' : event.object
            changeType = `${dataSourceType} ${event.type}`
            details = event.name;

            if (event.event === 'custom') {
                changeType = event.type
                details = event.description
            }

            eventsDataTable.push({
                id: event.id,
                instanceId: event.instanceId,
                event: event.event,
                changeType,
                changeFrom: event.oldvalue,
                changeTo: event.newvalue,
                htmlIconCode,
                instanceName: instance.name,
                detailsUrl,
                color,
                title,
                details,
                isHost: !!event.isHost,
                timeslice: event.timeslice,
                startedEnded: event.startedended,
                statistic: event.statistic,
                threshold: event.threshold,
                decreasing: event.decreasing,
                units: event.units,
                timesliceEnd: ((event.isPlotband === true) ? event.endDateTime : undefined),
                name: event.name,
                severity: event.severity
            });
        });

        const peakChanges = changesData.reduce((max, current) => Math.max(max, current[1]), 0);

        const uniqueTimeslices = Helper.extractUniqueTimeslices(alertsData, eventsData, changesData);
        // const plotBands = Helper.getChartPlotBandsCombinedV2(combinedEvents, period.ui.current.chartCategories);

        setChangeChartData(Helper.populateMissingTimeslices(changesData, uniqueTimeslices))
        setEventChartData(Helper.populateMissingTimeslices(eventsData, uniqueTimeslices))
        setAlertChartData(Helper.populateMissingTimeslices(alertsData, uniqueTimeslices))

        // setEventChartDataCombined(eventChartDataCombined)
        setCombinedChanges(combinedEvents)
        setPeakChanges(peakChanges)
        setEventsDataTable(eventsDataTable)
        // setPlotBands(plotBands)
        setLoading(true)
    }

    const chartSeries = useMemo<EChartTimeSeries[]>(() => [
        { color: COLOUR_CHANGES, chartType: 'bar', data: changeChartData as EChartPoint[], name: 'Changes' },
        { color: COLOUR_EVENTS, chartType: 'bar', data: eventChartData as EChartPoint[], name: 'Custom Events' },
        { color: COLOUR_ALERTS, chartType: 'bar', data: alertChartData as EChartPoint[], name: 'Alerts' },
    ], [changeChartData, eventChartData, alertChartData]);

    const chart = (
        <TimeSeriesChart series={chartSeries} tooltipType={CHART_DATA_TYPE.GENERIC} legend={true}/>
    );

    const totalChanges = combinedChanges.filter((event: any) => event.event === 'change').length
    const totalEvents = combinedChanges.filter((event: any) => event.event === 'custom').length
    const totalAlerts = combinedChanges.filter((event: any) => event.event === 'alert').length

    return (
            <div>
                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3">

                    {/* Option 1: Three separate WidgetCards (current implementation) */}
                    <WidgetCard
                        name={"Changes"}
                        iconClassName="changes"
                        icon="fa-exchange"
                        value={totalChanges}
                        cardTooltipText="The total auto-detected changes count"
                    />
                    <WidgetCard
                        name={"Custom Events"}
                        iconClassName="calendar"
                        icon="fa-calendar"
                        value={totalEvents}
                        cardTooltipText="The total custom events count"
                    />
                    <WidgetCard
                        name={"Alerts"}
                        iconClassName="alerts"
                        icon="fa-exclamation-triangle"
                        value={totalAlerts}
                        cardTooltipText="The total alerts count"
                    />

                    {/* Option 2: Single WidgetCard with multiple values (alternative implementation)
                    <WidgetCard
                        name={"Events"}
                        values={[
                            {
                                icon: 'fa-exchange',
                                value: totalChanges,
                                tooltip: 'Changes'
                            },
                            {
                                icon: 'fa-calendar',
                                value: totalEvents,
                                tooltip: 'Custom Events'
                            },
                            {
                                icon: 'fa-exclamation-triangle',
                                value: totalAlerts,
                                tooltip: 'Alerts'
                            }
                        ]}
                    />
                    */}
                </div>

                <div className="row">
                    <div className="col">
                        <div className="card collapsible">
                            <div className="card-header">
                                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                                Changes and events over time
                                <span className="badge bg-info"
                                      data-tooltip-id="global-tooltip" data-tooltip-content="Total change count">{eventsDataTable.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                   data-bs-target="#collapseCharts" aria-expanded="false"
                                   aria-controls="collapseCharts"/>
                            </div>
                            <div id="collapseCharts" className="card-body collapse show p-0">
                                <div className="row">
                                    <div className="col p-3">
                                        <h4>Changes and events<span
                                                className="peak">Peak Changes: {peakChanges}</span>
                                        </h4>
                                        {chart}
                                    </div>
                                </div>
                            </div>
                        </div>


                        <div className="card">
                            <div className="card-header">
                                <i className="fal fa-exchange fa-fw" aria-hidden="true"/>
                                Change and event history
                                <span className="badge bg-info"
                                      data-tooltip-id="global-tooltip" data-tooltip-content="Total change count">{combinedChanges.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                                <div className="btn-group float-end">
                                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                       data-bs-target="#collapseChanges" aria-expanded="false"
                                       aria-controls="collapseChanges"/>
                                </div>
                            </div>
                            <div id="collapseChanges" className="card-body collapse show">
                                {Number(process.env.REACT_APP_API_LIMIT) - 1 === eventsDataTable.length - 1 && (
                                        <Alert
                                                message={`To help preserve performance, we limit the total number of changes records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by their recorded change/event date and time.`}
                                                heading="Change and event records" variant="alert-info"/>
                                )}
                                <EventsTable loading={loading} eventsDataTable={eventsDataTable}/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    );
}
export default Changes;
